import type { StatusTone } from '../../../design-system';

export const gettingStartedCoreItemIds = ['company', 'employee-number', 'reference-data', 'organization-people', 'administrators'] as const;
export const gettingStartedItemIds = [...gettingStartedCoreItemIds, 'absence'] as const;
export type GettingStartedItemId = typeof gettingStartedItemIds[number];
export type GettingStartedStatus = 'missing' | 'needs-review' | 'in-progress' | 'awaiting-approval' | 'action-required' | 'complete' | 'forbidden';
export type GettingStartedCheck = { id: string; ready: boolean; count?: number };
export type GettingStartedItem = {
  id: GettingStartedItemId;
  status: GettingStartedStatus;
  canAcknowledge: boolean;
  fingerprint: string;
  checks: GettingStartedCheck[];
  acknowledgedAt?: string;
  scopeDescription?: string;
  canEditScope?: boolean;
  acknowledgmentVersion?: string;
};
export type GettingStartedSnapshot = {
  asOf: string;
  rulesVersion: string;
  completedCount: number;
  totalCount: number;
  items: GettingStartedItem[];
};
export type GettingStartedAcknowledgment = {
  itemId: GettingStartedItemId;
  asOf: string;
  fingerprint: string;
  scopeDescription?: string;
  expectedAcknowledgmentVersion?: string;
};
export type GettingStartedDestination = 'company' | 'employee-number' | 'locations' | 'job-profiles' | 'job-types' | 'organizations' | 'people' | 'administrators' | 'approval-settings' | 'absence-types' | 'absence-plans' | 'absence-calendars' | 'absence-operations' | 'absence-approval-settings' | 'absence-setup';
export type GettingStartedLink = { label: string; destination: GettingStartedDestination; scopes: string[] };
export type GettingStartedLocation = { asOf?: string; item?: GettingStartedItemId };
export type GettingStartedRoutePort = {
  read(): GettingStartedLocation;
  setAsOf(asOf: string): void;
  open(destination: GettingStartedDestination, location: Required<GettingStartedLocation>): void;
  subscribe(listener: () => void): () => void;
};

export function validGettingStartedDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.valueOf()) && date.toISOString().slice(0, 10) === value;
}

export const gettingStartedStatusPresentation: Record<GettingStartedStatus, { label: string; tone: StatusTone }> = {
  missing: { label: '미설정', tone: 'neutral' },
  'needs-review': { label: '확인 필요', tone: 'warning' },
  'in-progress': { label: '진행 중', tone: 'info' },
  'awaiting-approval': { label: '승인 대기', tone: 'warning' },
  'action-required': { label: '조치 필요', tone: 'danger' },
  complete: { label: '완료', tone: 'success' },
  forbidden: { label: '조회 권한 필요', tone: 'neutral' },
};

export const gettingStartedCards: Record<GettingStartedItemId, {
  title: string;
  description: string;
  links: GettingStartedLink[];
}> = {
  company: {
    title: '회사 정보', description: '임직원을 등록할 회사가 맞는지 확인해 주세요.',
    links: [{ label: '회사 정보 보기', destination: 'company', scopes: ['tenant.settings.read'] }],
  },
  'employee-number': {
    title: '사번 정책', description: '신규 입사자에게 적용할 사번 발급 규칙을 확인해 주세요.',
    links: [{ label: '사번 정책 보기', destination: 'employee-number', scopes: ['tenant.settings.read'] }],
  },
  'reference-data': {
    title: '등록 기준 정보', description: '근무지·직무·고용 유형을 준비해 주세요. 유효한 기본값을 그대로 사용할 수 있습니다.',
    links: [
      { label: '근무지 보기', destination: 'locations', scopes: ['tenant.settings.read', 'staffing.read'] },
      { label: '직무 보기', destination: 'job-profiles', scopes: ['tenant.settings.read', 'staffing.read'] },
      { label: '고용 유형 보기', destination: 'job-types', scopes: ['tenant.settings.read', 'staffing.read'] },
    ],
  },
  'organization-people': {
    title: '조직과 구성원', description: '조직·구성원을 직접 등록하고, 필요한 소속과 조직장이 반영됐는지 확인해 주세요.',
    links: [
      { label: '조직 보기', destination: 'organizations', scopes: ['staffing.read'] },
      { label: '구성원 보기', destination: 'people', scopes: ['staffing.read'] },
    ],
  },
  administrators: {
    title: '운영 시작 전 확인', description: '앞선 준비 항목을 마친 뒤 회사 관리자와 조직·입사 요청의 승인 담당자를 확인해 주세요.',
    links: [
      { label: '관리자 설정 보기', destination: 'administrators', scopes: ['security.read'] },
      { label: '승인 정책 보기', destination: 'approval-settings', scopes: ['workflow.read'] },
    ],
  },
  absence: {
    title: '휴가 설정', description: '휴가 준비 상태를 확인해 주세요.',
    links: [
      { label: '휴가 정책 보기', destination: 'absence-types', scopes: ['absence.read'] },
      { label: '휴일 캘린더 보기', destination: 'absence-calendars', scopes: ['absence.read'] },
      { label: '휴가 운영 보기', destination: 'absence-operations', scopes: ['absence.read', 'staffing.read'] },
      { label: '휴가 승인 경로 보기', destination: 'absence-approval-settings', scopes: ['workflow.read'] },
    ],
  },
};

export function gettingStartedCheckLabel(check: GettingStartedCheck): string {
  const labels: Record<string, string> = {
    companies: '등록할 회사', 'employee-number-policy': '사번 발급 정책',
    locations: '유효한 근무지', 'job-profiles': '유효한 직무', 'job-families': '직군 연결', 'employee-job-types': '임직원 고용 유형',
    'root-organizations': '최상위 조직', organizations: '반영된 조직', workers: '반영된 구성원',
    'missing-primary-assignments': '기본 소속이 없는 구성원', 'pending-staffing': '승인·처리 중인 인사 요청',
    'vacant-managers': '조직장이 공석인 조직', administrators: '회사 관리자',
    'core-hr-approvers': '준비된 기본 승인 경로', 'required-items': '앞선 필수 항목 확인',
    'absence-types': '사용할 휴가 유형', 'absence-plans': '유효한 발생 정책',
    'absence-targets': '정책이 적용된 대상', 'absence-missing-entitlements': '잔액이 준비되지 않은 대상',
    'absence-conflicting-entitlements': '중복된 휴가 잔액',
    'absence-holiday-calendars': '연결된 휴일 캘린더', 'absence-approvers': '휴가 승인 경로',
  };
  return labels[check.id] ?? '준비 조건';
}

export function gettingStartedCheckDescription(check: GettingStartedCheck): string | undefined {
  if (check.ready) return undefined;
  const descriptions: Record<string, string> = {
    'absence-types': '조회 기준일에 사용할 수 있는 휴가 유형이 없습니다.',
    'absence-plans': '조회 기준일에 유효한 발생 정책이 없습니다.',
    'absence-targets': '정책이 적용된 대상이 없습니다. 적용 범위와 제외 대상을 확인해 주세요.',
    'absence-missing-entitlements': '정책에 맞는 휴가 잔액 정보가 준비되지 않은 대상이 있습니다.',
    'absence-conflicting-entitlements': '같은 구성원·휴가 유형에 잔액이 중복돼 휴가를 신청할 수 없습니다.',
    'absence-holiday-calendars': '사용 중인 정책에 연결된 휴일 캘린더가 유효하지 않습니다.',
    'absence-approvers': '승인이 필요한 휴가의 승인 경로나 담당자가 준비되지 않았습니다.',
  };
  return descriptions[check.id];
}

export function gettingStartedCheckOptionalLabel(check: GettingStartedCheck): string | undefined {
  if (!check.ready || check.count !== 0) return undefined;
  if (check.id === 'absence-holiday-calendars') return '사용 안 함';
  if (check.id === 'absence-approvers') return '해당 없음';
  return undefined;
}

export function gettingStartedAbsenceCheckDestination(check: GettingStartedCheck): GettingStartedDestination | undefined {
  const destinations: Record<string, GettingStartedDestination> = {
    'absence-types': 'absence-types',
    'absence-plans': 'absence-types',
    'absence-targets': 'absence-types',
    'absence-missing-entitlements': 'absence-operations',
    'absence-conflicting-entitlements': 'absence-operations',
    'absence-holiday-calendars': 'absence-calendars',
    'absence-approvers': 'absence-approval-settings',
  };
  return destinations[check.id];
}

export function gettingStartedCheckCountUnit(check: GettingStartedCheck): string {
  if (['workers', 'missing-primary-assignments', 'administrators'].includes(check.id)) return '명';
  if (['absence-targets', 'absence-missing-entitlements', 'absence-conflicting-entitlements'].includes(check.id)) return '건';
  return '개';
}
